import React, { useState, useRef } from "react";
import { useAuth } from "../auth/AuthContext";
import { useNavigate } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import "./JoinPage.css";

const SITE_KEY = "6Lfga7crAAAAAC1OwLDPGdW_R3hE_2h6GcN97Mv3";
const USER_SIGNUP_URL = "https://script.google.com/macros/s/AKfycbztpbpnfYpX9EmoIvRPEZ7vygAadSYi78X5JMT9KFh_Ys78SNi22Z7qQP5iW5lnmy6g/exec";
const APPLICATION_URL = "https://script.google.com/macros/s/AKfycbztpbpnfYpX9EmoIvRPEZ7vygAadSYi78X5JMT9KFh_Ys78SNi22Z7qQP5iW5lnmy6g/exec?url=applications";

const JoinPage: React.FC = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "Member", message: "" });
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [isApplying, setIsApplying] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  
  // Email verification states
  const [showVerification, setShowVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [verifyingEmail, setVerifyingEmail] = useState(false);
  const [pendingEmail, setPendingEmail] = useState("");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    
    // Reset application mode when role changes
    if (name === 'role') {
      setIsApplying(false);
    }
  };

  const handleCaptcha = (token: string | null) => {
    setCaptchaToken(token);
  };

  const handleSendVerification = async () => {
    if (!form.name || !form.email || !form.password) {
      setError("Please fill in all required fields");
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const response = await fetch('/api/send-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          password: form.password,
          role: form.role
        }),
      });

      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);
      console.log('Response headers:', response.headers);
      
      const responseText = await response.text();
      console.log('Raw response:', responseText);
      
      let data;
      try {
        data = JSON.parse(responseText);
        console.log('Parsed response data:', data);
      } catch (parseError) {
        console.error('JSON parse error:', parseError);
        console.log('Response was not JSON:', responseText);
        setError("Server error. Please try again.");
        return;
      }

      if (response.ok) {
        setShowVerification(true);
        setPendingEmail(form.email);
        setSuccess("Verification code sent to your email!");
        console.log('Verification flow initiated successfully');
      } else {
        console.log('Response not ok, error:', data.message);
        setError(data.message || "Failed to send verification code");
      }
    } catch (error) {
      console.error('Verification send error:', error);
      console.error('Error details:', JSON.stringify(error, Object.getOwnPropertyNames(error)));
      setError("Failed to send verification code. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode) {
      setError("Please enter the verification code");
      return;
    }

    setVerifyingEmail(true);
    setError(null);

    try {
      const response = await fetch('/api/verify-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: pendingEmail,
          code: verificationCode
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // Registration successful, log the user in
        login({
          name: form.name,
          email: form.email,
          role: form.role
        });
        setSuccess("Email verified! Welcome to WellBnB™!");
        setTimeout(() => navigate('/'), 1500);
      } else {
        setError(data.message || "Invalid verification code");
      }
    } catch (error) {
      console.error('Verification error:', error);
      setError("Verification failed. Please try again.");
    } finally {
      setVerifyingEmail(false);
    }
  };

  const handleResendCode = async () => {
    await handleSendVerification();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    // Skip CAPTCHA validation in development
    const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname.includes('replit');
    if (!isDevelopment && !captchaToken && !isLogin) {
      setError("Please complete the CAPTCHA.");
      return;
    }

    setSubmitting(true);

    try {
      if (isLogin) {
        // Temporary mock login for testing - bypassing backend issues
        console.log('Mock login with:', { email: form.email, password: '***' });
        
        // Simulate login validation
        if (form.email && form.password) {
          // Pass user data to login function
          login({
            name: "WellBnB User", // In production, this would come from the backend
            email: form.email,
            role: "Member" // Default role for login
          });
          setSuccess("Login successful! Welcome to WellBnB!");
          setTimeout(() => navigate('/'), 1500);
        } else {
          setError("Please enter both email and password");
        }
      } else if (isApplying && (form.role === 'Partner' || form.role === 'Concierge' || form.role.includes('Partner') || form.role.includes('Concierge'))) {
        // Partner/Concierge application flow
        const res = await fetch(APPLICATION_URL, {
          method: "POST",
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: form.name,
            email: form.email,
            requestedRole: form.role,
            message: form.message
          }),
        });
        
        if (res.ok) {
          setSuccess(`Your ${form.role} application has been submitted! We'll review it and get back to you soon.`);
          setForm({ name: "", email: "", password: "", role: "Member", message: "" });
          // Redirect to HomePage after successful application submission
          setTimeout(() => navigate('/'), 2000);
        } else {
          setError("Application submission failed. Please try again.");
        }
      } else {
        // For new registrations, send verification code instead of direct registration
        await handleSendVerification();
        return; // Exit early, verification flow will handle the rest
      }
      
      setCaptchaToken(null);
      recaptchaRef.current?.reset();
    } catch (error) {
      console.error('Submit error:', error);
      console.error('Error details:', error instanceof Error ? error.message : error);
      setError(isLogin ? "Login failed. Please try again." : "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="join-page">
      <form className="join-form" onSubmit={handleSubmit}>
        <div className="logo-container">
          <img
            src="/public_logo.png"
            alt="WellBnB Logo"
            className="logo"
          />
        </div>
        <div className="auth-tabs">
          <button 
            type="button"
            className={`tab-button ${!isLogin ? 'active' : ''}`}
            onClick={() => setIsLogin(false)}
          >
            Join
          </button>
          <button 
            type="button"
            className={`tab-button ${isLogin ? 'active' : ''}`}
            onClick={() => setIsLogin(true)}
          >
            Login
          </button>
        </div>
        
        <h2>Welcome to WellBnB<span style={{fontSize: '0.6em', verticalAlign: 'super'}}>™</span></h2>
        <div className="subtitle">
          {isLogin 
            ? "Welcome back! What well would you like to experience today?!" 
            : "Sign up to access personalized wellness travel experiences!"
          }
        </div>
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}
        
        {!isLogin && (
          <>
            <div className="form-group">
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                required
                autoComplete="off"
                placeholder="Name"
              />
            </div>
            <div className="form-group">
              <select
                name="role"
                value={form.role}
                onChange={handleChange}
                required
                className="role-select"
              >
                <option value="Member">Member - Access wellness content & bookings</option>
                <option value="Partner">Partner - List your wellness properties</option>
                <option value="Concierge">Concierge - Provide wellness services</option>
                <option value="Member; Partner">Member & Partner - Book travel + list properties</option>
                <option value="Member; Concierge">Member & Concierge - Book travel + provide services</option>
                <option value="Partner; Concierge">Partner & Concierge - List properties + provide services</option>
                <option value="Member; Partner; Concierge">All Roles - Full platform access</option>
              </select>
            </div>
          </>
        )}
        <div className="form-group">
          <input
            name="email"
            type="email"
            value={form.email}
            onChange={handleChange}
            required
            autoComplete="off"
            placeholder="Email"
          />
        </div>
        <div className="form-group">
          <input
            name="password"
            type="password"
            value={form.password}
            onChange={handleChange}
            required
            autoComplete="off"
            placeholder="Password"
          />
        </div>
        
        {/* Partner/Concierge Application Fields */}
        {!isLogin && (form.role === 'Partner' || form.role === 'Concierge' || form.role.includes('Partner') || form.role.includes('Concierge')) && (
          <div className="application-section">
            <div className="application-toggle">
              <button
                type="button"
                className={`toggle-button ${!isApplying ? 'active' : ''}`}
                onClick={() => setIsApplying(false)}
              >
                Sign Up as Member First
              </button>
              <button
                type="button"
                className={`toggle-button ${isApplying ? 'active' : ''}`}
                onClick={() => setIsApplying(true)}
              >
                Apply as {form.role.includes(';') ? 'Multiple Roles' : form.role}
              </button>
            </div>
            
            {isApplying && (
              <div className="form-group">
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  required={isApplying}
                  placeholder={`Tell us why you'd like to have ${form.role.includes(';') ? 'these roles' : 'this role'} on WellBnB...`}
                  rows={4}
                  className="application-message"
                />
              </div>
            )}
          </div>
        )}

        {/* Email Verification Step */}
        {showVerification && !isLogin && (
          <div className="verification-section">
            <div className="verification-header">
              <h3>Verify Your Email</h3>
              <p>We sent a 6-digit code to <strong>{pendingEmail}</strong></p>
            </div>
            
            <div className="form-group">
              <input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Enter 6-digit code"
                maxLength={6}
                className="verification-input"
                style={{
                  textAlign: 'center',
                  fontSize: '24px',
                  fontWeight: 'bold',
                  letterSpacing: '8px',
                  fontFamily: 'monospace'
                }}
              />
            </div>
            
            <div className="verification-actions">
              <button
                type="button"
                onClick={handleVerifyCode}
                disabled={verifyingEmail || verificationCode.length !== 6}
                className="verify-button"
              >
                {verifyingEmail ? "Verifying..." : "Verify Email"}
              </button>
              
              <button
                type="button"
                onClick={handleResendCode}
                disabled={submitting}
                className="resend-button"
              >
                {submitting ? "Sending..." : "Resend Code"}
              </button>
            </div>
          </div>
        )}

        {/* Only show CAPTCHA in production for registration */}
        {!isLogin && !showVerification && !(window.location.hostname === 'localhost' || window.location.hostname.includes('replit')) && (
          <div className="recaptcha-container">
            <ReCAPTCHA
              sitekey={SITE_KEY}
              onChange={handleCaptcha}
              ref={recaptchaRef}
            />
          </div>
        )}

        {/* Hide main submit button when showing verification */}
        {!showVerification && (
          <button type="submit" disabled={submitting} className="submit-button">
            {submitting 
              ? (isLogin ? "Signing in..." : isApplying ? "Submitting Application..." : "Sending Code...") 
              : (isLogin ? "Sign In" : isApplying ? `Apply as ${form.role}` : "Send Verification Code")
            }
          </button>
        )}
      </form>
    </div>
  );
};

export default JoinPage;